import React, { useState } from 'react';
import { useQuery } from '@apollo/client';

import { GET_TIPS } from '../../../../queries';
import Loading from '../../../modules/Loading';
import Tips from '../Tips';
import { Caption, Heading, Subtitle } from '../../../modules/Typography';
import TextField from '../../../modules/TextField';
import Button from '../../../modules/Button';

import { useFocus } from '../../../../utils/hooks';
import xCircleIcon from '../../../../assets/icon_x_circle.svg';

import validator from './validator';
import calculate from './formulas';

const initialResults = {
    height: 0,
    heightRounded: 0
};

const ActiveArea = props => {
    const { data, loading } = useQuery(GET_TIPS, { variables: { calculator: 'Active Area' } });

    const [width, setWidth] = useState('');
    const [aspectRatio, setAspectRatio] = useState('');
    const [results, setResults] = useState(initialResults);
    const [errors, setErrors] = useState({});

    const [widthRef, setWidthFocus] = useFocus();
    const [aspectRatioRef, setAspectRatioFocus] = useFocus();

    if (loading) return <Loading />;

    const handleCalculation = e => {
        e.preventDefault();

        const _errors = {};
        setErrors(_errors);

        const params = {
            width: width.length === 0 ? '' : Number(width),
            aspectRatio: aspectRatio.length === 0 ? '' : Number(aspectRatio)
        };

        const isValid = validator(params);
        if (!isValid) {
            for (const error of validator.errors) {
                const { instancePath, message } = error;
                _errors[instancePath] = { message };
            }

            setErrors(_errors);
            return;
        }

        const results = calculate(params);
        setResults(results);
    };

    const handleReset = e => {
        e.preventDefault();
        setWidth('');
        setAspectRatio('');
        setErrors({});
        setResults(initialResults);
    };

    const tips = data?.calculatorses.data[0]?.attributes.tips ?? [];
    return (
        <>
            <section className='calculator__wrapper'>

                <form className='calculator__form'>
                    <Heading number={5} heavy block className='calculator__form__title'>Active Area Calculator</Heading>

                    <div className='calculator__input__wrapper'>
                        <TextField
                            className='calculator__input'
                            label='Width'
                            type='text'
                            value={width}
                            onChange={e => setWidth(e.target.value)}
                            error={Boolean(errors['/width']) ?? false}
                            errorMessage={errors['/width']?.message}
                            ref={widthRef}
                            required
                        />
                        <Caption className='calculator__input__helper-text'>Enter full frame width in pixels</Caption>
                        {
                            width.length !== 0 && Boolean(errors['/width']) &&
                            <img
                                className='clear-input' alt='calculator icon'
                                src={xCircleIcon} width='14px' height='14px'
                                onClick={() => {
                                    setWidth('');
                                    setWidthFocus();
                                }}
                            />
                        }
                    </div>
                    <div className='calculator__input__wrapper last'>
                        <TextField
                            className='calculator__input'
                            label='Aspect Ratio'
                            type='text'
                            value={aspectRatio}
                            onChange={e => setAspectRatio(e.target.value)}
                            error={Boolean(errors['/aspectRatio']) ?? false}
                            errorMessage={errors['/aspectRatio']?.message}
                            ref={aspectRatioRef}
                            required
                        />
                        <Caption className='calculator__input__helper-text'>Enter Aspect Ratio (e.g. 2.39 or 2.38805)</Caption>
                        {
                            aspectRatio.length !== 0 && Boolean(errors['/aspectRatio']) &&
                            <img
                                className='clear-input' alt='calculator icon'
                                src={xCircleIcon} width='14px' height='14px'
                                onClick={() => {
                                    setAspectRatio('');
                                    setAspectRatioFocus();
                                }}
                            />
                        }
                    </div>

                    <div className='calculator__form__controls'>
                        <Button
                            className='calculator__form__control-calculate'
                            type='primary'
                            color='primary-light'
                            text='Calculate'
                            onClick={e => handleCalculation(e)}
                        />
                        <Button
                            className='calculator__form__control-reset'
                            type='secondary'
                            color='primary-light'
                            text='Reset'
                            onClick={e => handleReset(e)}
                        />
                    </div>
                </form>

                <aside className='calculator__results'>
                    <Heading number={5} heavy block className='calculator__results__title'>Results</Heading>

                    <ul>
                        <li className='calculator__result-item'>
                            <Subtitle heavy>Height</Subtitle>
                            <Subtitle heavy>{results.height}</Subtitle>
                        </li>
                        <li className='calculator__result-item'>
                            <Subtitle>Height (Rounded)</Subtitle>
                            <Subtitle>{results.heightRounded}</Subtitle>
                        </li>
                    </ul>

                </aside>
            </section>

            {
                !tips || tips.length === 0
                    ? null
                    : <Tips tips={tips} />
            }
        </>
    );
};

export default ActiveArea;
